import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { weakAreas } from '../../../quizzes/components/quiz-builder-pro-page/quiz-builder-pro.util';
import { AcsElement } from '../../../quizzes/models/quiz-builder-pro.model';

export const ACS_PICK_LIMIT = 25;
const WEAK = 'weak';
const ALL = 'all';

export interface AcsPickerRow { code: string; description: string; missed: string; checked: boolean; disabled: boolean; }
/** One Task's rows; `area` is set on the first group of each Area of Operation so the list shows it once. */
export interface AcsPickerGroup { key: string; area: string; task: string; rows: AcsPickerRow[]; }
export interface AcsPickerArea { key: string; label: string; count: number; selected: number; }

/**
 * Chooses the ACS codes AI drafts for. Edits stay in a draft until Apply; Cancel, Escape or new elements discard them.
 * ponytail: renders every matching row (low hundreds per ACS test); virtualize if a test ever lists thousands.
 */
@Component({
  selector: 'app-scenario-acs-picker',
  templateUrl: './scenario-acs-picker.component.html',
  styleUrls: ['./scenario-acs-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenarioAcsPickerComponent implements OnChanges {
  @Input() elements: AcsElement[] = [];
  @Input() selected: string[] = [];
  @Input() loading = false;
  @Input() disabled = false;
  @Output() readonly selectedChange = new EventEmitter<string[]>();
  @ViewChild('dialog', { static: true }) private dialog?: ElementRef<HTMLDialogElement>;

  readonly limit = ACS_PICK_LIMIT;
  summaryTitle = 'Any ACS code';
  summaryCodes = '';
  triggerDisabled = true;

  isOpen = false;
  query = '';
  area = ALL;
  reviewing = false;
  rail: AcsPickerArea[] = [];
  groups: AcsPickerGroup[] = [];
  draftCount = 0;
  applyLabel = 'Use any code';
  emptyText = '';
  private draft = new Set<string>();
  private weak: AcsElement[] = [];

  constructor(private readonly cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    // New course data means the draft names codes that may no longer exist; drop it rather than apply it.
    if (changes.elements) {
      this.close();
      this.weak = weakAreas(this.elements, 10);
    }
    const count = this.selected.length;
    this.summaryTitle = count ? `${count} ACS code${count > 1 ? 's' : ''}` : this.loading ? 'Loading ACS codes…'
      : this.elements.length ? 'Any ACS code' : 'No ACS codes';
    this.summaryCodes = this.selected.join(', ');
    this.triggerDisabled = this.disabled || this.loading || !this.elements.length;
  }

  open(): void {
    if (this.triggerDisabled || this.isOpen) { return; }
    this.draft = new Set(this.selected);
    this.query = '';
    this.reviewing = false;
    this.area = this.weak.length ? WEAK : ALL;
    this.isOpen = true;
    this.refresh();
    // Render the content first so showModal can move focus to the search field.
    this.cdr.detectChanges();
    this.dialog?.nativeElement.showModal?.();
  }

  /** Cancel, the close button and Escape (native dialog close) all land here; nothing is applied. */
  close(): void {
    if (this.dialog?.nativeElement.open) { this.dialog.nativeElement.close(); }
    this.isOpen = false;
    this.draft = new Set();
    this.groups = [];
    this.cdr.markForCheck();
  }

  apply(): void {
    if (!this.isOpen) { return; }
    this.selectedChange.emit(this.elements.filter(e => this.draft.has(e.code)).map(e => e.code));
    this.close();
  }

  toggle(code: string): void {
    if (this.draft.has(code)) {
      this.draft.delete(code);
    } else if (this.draft.size < this.limit) {
      this.draft.add(code);
    }
    this.refresh();
  }

  search(query: string): void {
    this.query = query;
    this.reviewing = false;
    if (query.trim()) { this.area = ALL; }
    this.refresh();
  }

  setArea(key: string): void {
    this.area = key;
    this.reviewing = false;
    this.refresh();
  }

  toggleReview(): void {
    this.reviewing = !this.reviewing;
    this.refresh();
  }

  clear(): void {
    this.draft.clear();
    this.refresh();
  }

  trackByKey(_: number, item: { key: string }): string { return item.key; }
  trackByCode(_: number, row: AcsPickerRow): string { return row.code; }

  private refresh(): void {
    const term = this.query.trim().toLowerCase();
    const matches = (e: AcsElement) => !term || [e.code, e.description, e.task, e.area].some(v => v && v.toLowerCase().includes(term));
    const found = this.elements.filter(matches);
    const atLimit = this.draft.size >= this.limit;
    const row = (e: AcsElement): AcsPickerRow => {
      const checked = this.draft.has(e.code);
      return { code: e.code, description: e.description, missed: e.missRate ? `${e.missRate}% missed` : '',
        checked, disabled: !checked && atLimit };
    };

    const areas = new Map<string, AcsPickerArea>();
    this.elements.forEach(e => {
      const key = e.area || '';
      const area = areas.get(key) || { key, label: key || 'Other', count: 0, selected: 0 };
      if (matches(e)) { area.count++; }
      if (this.draft.has(e.code)) { area.selected++; }
      areas.set(key, area);
    });
    const weak = this.weak.filter(matches);
    const weakSelected = this.weak.filter(e => this.draft.has(e.code)).length;
    this.rail = [
      ...(this.weak.length ? [{ key: WEAK, label: 'Most missed', count: weak.length, selected: weakSelected }] : []),
      { key: ALL, label: 'All areas', count: found.length, selected: this.draft.size },
      ...areas.values(),
    ];

    if (this.reviewing) {
      this.groups = this.group(this.elements.filter(e => this.draft.has(e.code)), row);
    } else if (this.area === WEAK) {
      // Ranked by miss rate, so not grouped by task.
      this.groups = weak.length ? [{ key: WEAK, area: '', task: '', rows: weak.map(row) }] : [];
    } else {
      this.groups = this.group(this.area === ALL ? found : found.filter(e => (e.area || '') === this.area), row);
    }
    this.emptyText = this.groups.length ? '' : this.reviewing ? 'No codes selected.'
      : term ? `No codes match “${this.query.trim()}”.` : 'No codes in this area.';
    this.draftCount = this.draft.size;
    this.applyLabel = this.draftCount ? `Apply ${this.draftCount} code${this.draftCount > 1 ? 's' : ''}` : 'Use any code';
    this.cdr.markForCheck();
  }

  private group(list: AcsElement[], row: (e: AcsElement) => AcsPickerRow): AcsPickerGroup[] {
    const groups: AcsPickerGroup[] = [];
    let lastArea: string | undefined;
    list.forEach(e => {
      const key = `${e.area || ''}|${e.task || ''}`;
      let current = groups[groups.length - 1];
      if (!current || current.key !== key) {
        current = { key, area: e.area !== lastArea ? e.area || '' : '', task: e.task || '', rows: [] };
        lastArea = e.area;
        groups.push(current);
      }
      current.rows.push(row(e));
    });
    return groups;
  }
}
